import { Controller } from "@hotwired/stimulus"
import { useClickOutside, useTransition } from "stimulus-use"
import {
  trapFocus,
  releaseFocusTrap,
  lockBodyScroll,
  unlockBodyScroll,
  lockBackground,
  unlockBackground,
  getNestedModalParent,
  lockStackedParent,
  unlockStackedParent,
} from "../../../utils/FocusTrap"
import { ACTIVATION_KEYS } from "../../../utils/keyboardAccessibility"

const CLOSE_KEYS = ACTIVATION_KEYS
const OPEN_ACTIONS = ["click->railsui-modal#open", "click->railsui-modal#open:prevent"]
const CLOSE_BUTTON_SELECTOR = ".aquifer-modal__close"
const MODAL_CLOSE_ACTION_PATTERN = /\bclick->railsui-modal#close\b/
const MODAL_FOCUSABLE_EXCLUSION =
  ':not(:disabled):not([disabled]):not([hidden]):not(.hidden):not([aria-hidden="true"])'
const MODAL_CONTENT_FOCUS_SELECTOR = [
  `[data-railsui-dropdown-menu-target="trigger"]${MODAL_FOCUSABLE_EXCLUSION}`,
  `input${MODAL_FOCUSABLE_EXCLUSION}:not([type="hidden"])`,
  `select${MODAL_FOCUSABLE_EXCLUSION}`,
  `textarea${MODAL_FOCUSABLE_EXCLUSION}`,
  `button:not(.aquifer-modal__close)${MODAL_FOCUSABLE_EXCLUSION}`,
  `a[href]${MODAL_FOCUSABLE_EXCLUSION}`,
].join(", ")
const BODY_SCROLL_LOCK_CLASS = "modal-active"
const SCROLLBAR_COMPENSATION_VAR = "--railsui-modal-scrollbar-compensation"
// Compact educator-home AI keeps its container open without locking page scroll.
const OPEN_LOCKING_MODAL_CONTAINER_SELECTOR =
  "[data-railsui-modal-target='container']:not(.hidden):not([data-railsui-modal-skip-body-scroll-lock='true'])"

const applyBodyScrollCompensation = (scrollbarWidth) => {
  document.body.classList.add(BODY_SCROLL_LOCK_CLASS)
  if (scrollbarWidth > 0) {
    const value = `${scrollbarWidth}px`
    document.body.style.paddingRight = value
    // Fixed `right:0` UI (compact AI chat) does not inherit body padding — offset via var.
    document.documentElement.style.setProperty(SCROLLBAR_COMPENSATION_VAR, value)
  } else {
    document.body.style.removeProperty("padding-right")
    document.documentElement.style.removeProperty(SCROLLBAR_COMPENSATION_VAR)
  }
}

const clearBodyScrollCompensation = () => {
  document.body.classList.remove(BODY_SCROLL_LOCK_CLASS)
  document.body.style.removeProperty("padding-right")
  document.documentElement.style.removeProperty(SCROLLBAR_COMPENSATION_VAR)
}

const syncBodyScrollLock = () => {
  const anyOpen = Boolean(document.querySelector(OPEN_LOCKING_MODAL_CONTAINER_SELECTOR))

  if (anyOpen) {
    // Already locked — keep padding/var; re-measure would read 0 after overflow:hidden.
    if (document.body.classList.contains(BODY_SCROLL_LOCK_CLASS)) return

    applyBodyScrollCompensation(window.innerWidth - document.documentElement.clientWidth)
  } else {
    clearBodyScrollCompensation()
  }
}

export default class extends Controller {
  static targets = ["container", "content"]

  static values = {
    wide: Boolean,
    initialFocusSelector: String,
    initialFocusDialog: Boolean,
    skipInitialFocus: { type: Boolean, default: false },
    clickOutside: { type: Boolean, default: true },
  }

  connect() {
    this.handleCloseKeydown = this.handleCloseKeydown.bind(this)
    this.handleCloseKeyup = this.handleCloseKeyup.bind(this)
    this.previouslyFocusedElement = null
    this.closeKeyArmed = false
    this.stackedParent = null
    this.suppressClickOutside = false

    // useTransition chains toggleTransition if called twice on the same instance
    // (portal reconnect can reuse the controller). Wire once per lifetime.
    if (!this.transitionWired) {
      useTransition(this, {
        element: this.contentTarget,
      })
      this.transitionWired = true
    }

    if (!this.clickOutsideWired) {
      useClickOutside(this, {
        element: this.contentTarget,
      })
      this.clickOutsideWired = true
    }

    this.element.addEventListener("keydown", this.handleCloseKeydown)
    this.element.addEventListener("keyup", this.handleCloseKeyup)

    if (this.isOpen) {
      this.suppressClickOutside = true
      this.previouslyFocusedElement = this.focusTriggerElement(null)
      this.enter()
      this.activateFocusTrap()
      syncBodyScrollLock()
      queueMicrotask(() => {
        requestAnimationFrame(() => {
          this.suppressClickOutside = false
        })
      })
    }
  }

  open(event) {
    event?.preventDefault()

    if (this.isOpen) {
      this.previouslyFocusedElement = this.focusTriggerElement(event)
      this.close(event)
      return
    }

    this.reveal(event)
  }

  show(event) {
    event?.preventDefault()

    if (this.isOpen) {
      this.previouslyFocusedElement =
        this.previouslyFocusedElement || this.focusTriggerElement(event)
      this.setOpenState(true)
      this.applyInitialFocus()
      return
    }

    this.reveal(event)
  }

  reveal(event) {
    this.previouslyFocusedElement = this.focusTriggerElement(event)
    // Same-gesture clickOutside (capture/touchend) must not dismiss a just-opened modal.
    this.suppressClickOutside = true
    this.setOpenState(true)
    syncBodyScrollLock()
    // Always enter — never toggleTransition. Chained toggles from double useTransition
    // enter then leave in one call (content flashes then hides under open overlay).
    this.enter()
    this.stackAboveOpenParentIfNeeded()
    this.scheduleFocusTrap()
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        this.suppressClickOutside = false
      })
    })
  }

  close(event) {
    event?.preventDefault()
    event?.stopPropagation()

    this.suppressClickOutside = false
    this.releaseStackedParent()
    this.deactivateFocusTrap()
    this.leave()
    this.setOpenState(false)
    syncBodyScrollLock()
    this.restoreFocus()
    this.dispatch("closed")
  }

  stackAboveOpenParentIfNeeded() {
    const parentContainer = getNestedModalParent(this.element.id || null)
    if (!parentContainer || parentContainer === this.containerTarget) return
    if (parentContainer.contains(this.element)) return

    lockStackedParent(parentContainer, this.containerTarget)
    this.stackedParent = parentContainer
  }

  releaseStackedParent() {
    if (!this.stackedParent && !this.hasContainerTarget) return

    unlockStackedParent(this.stackedParent, this.hasContainerTarget ? this.containerTarget : null)
    this.stackedParent = null
  }

  clickOutside(event) {
    if (!this.clickOutsideValue) return
    if (!this.isOpen) return
    if (this.suppressClickOutside) return
    if (this.isBlockedByNestedModal()) return
    if (this.contentTarget.contains(event.target)) return

    const action = event.target?.closest?.("[data-action]")?.dataset?.action || ""
    if (OPEN_ACTIONS.some((openAction) => action.includes(openAction))) return

    this.close(event)
  }

  closeWithEsc(event) {
    if (event?._railsuiModalEscapeHandled) return
    if (this.isBlockedByNestedModal()) return

    const isEscape = event.key === "Escape" || event.keyCode === 27
    if (isEscape && this.isOpen) {
      event._railsuiModalEscapeHandled = true
      event.stopImmediatePropagation?.()
      this.close(event)
    }
  }

  isBlockedByNestedModal() {
    return Boolean(this.hasContainerTarget && this.containerTarget.inert)
  }

  handleCloseKeydown(event) {
    const closeKey = this.closeKeyPressed(event)
    if (closeKey) {
      event.preventDefault()
      event.stopPropagation()
      this.closeKeyArmed = true
    }
  }

  handleCloseKeyup(event) {
    const closeKey = this.closeKeyPressed(event)
    const willClose = closeKey && this.closeKeyArmed
    if (willClose) {
      this.close(event)
    }

    if (CLOSE_KEYS.includes(event.key)) {
      this.closeKeyArmed = false
    }
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.handleCloseKeydown)
    this.element.removeEventListener("keyup", this.handleCloseKeyup)

    this.suppressClickOutside = false
    this.releaseStackedParent()

    // Avoid toggleTransition() on teardown: closed→enter corrupts portal reconnect;
    // open→leave hides content but leaves the overlay mounted.
    if (this.isOpen) {
      this.deactivateFocusTrap()
      if (this.transitioned) this.leave()
      this.setOpenState(false)
    } else {
      releaseFocusTrap(this.trapElement, { restoreFocus: false })
      if (this.hasContentTarget) this.contentTarget.classList.add("hidden")
      this.transitioned = false
    }

    syncBodyScrollLock()
  }

  get trapElement() {
    return this.containerTarget
  }

  setOpenState(open) {
    if (this.wideValue) {
      this.containerTarget.classList.toggle("bg-black/50", open)
    } else {
      this.containerTarget.classList.toggle("aquifer-modal-overlay", open)
    }
    this.containerTarget.classList.toggle("hidden", !open)
    this.containerTarget.setAttribute("aria-hidden", String(!open))

    if (!open) {
      this.contentTarget.classList.add("hidden")
    }
  }

  get isOpen() {
    return !this.containerTarget.classList.contains("hidden")
  }

  activateFocusTrap() {
    this.scheduleFocusTrap()
  }

  scheduleFocusTrap() {
    this.focusTrapGeneration = (this.focusTrapGeneration || 0) + 1
    const generation = this.focusTrapGeneration

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Cancelled by deactivateFocusTrap / a newer scheduleFocusTrap (compact open race).
        if (generation !== this.focusTrapGeneration) return
        if (!this.isOpen) return

        this.contentTarget.classList.remove("hidden")

        const preservedScrollTop = this.containerTarget.scrollTop
        this.containerTarget.scrollTop = 0
        if (preservedScrollTop > 0) {
          this.containerTarget._stackedScrollTop = preservedScrollTop
        }

        lockBodyScroll()
        lockBackground(this.element)
        trapFocus(this.trapElement, {
          skipInitialFocus: true,
          previousFocusElement: this.previouslyFocusedElement,
        })

        this.applyInitialFocus()
      })
    })
  }

  applyInitialFocus() {
    if (!this.isOpen) return
    if (this.skipInitialFocusValue) return

    this.contentTarget.classList.remove("hidden")

    if (this.initialFocusDialogValue) {
      this.ensureDialogProgrammaticFocus()
      this.focusElement(this.containerTarget)
      return
    }

    const toFocus =
      this.focusTargetFromSelector(this.initialFocusSelectorValue) ||
      this.contentTarget.querySelector('[data-railsui-dropdown-menu-target="trigger"]:not(:disabled)') ||
      this.contentTarget.querySelector(CLOSE_BUTTON_SELECTOR) ||
      this.contentTarget.querySelector(MODAL_CONTENT_FOCUS_SELECTOR)

    this.focusElement(toFocus)
  }

  ensureDialogProgrammaticFocus() {
    if (!this.hasContainerTarget) return

    if (!this.containerTarget.hasAttribute("tabindex")) {
      this.containerTarget.setAttribute("tabindex", "-1")
    }
  }

  focusElement(element) {
    if (!(element instanceof HTMLElement) || typeof element.focus !== "function") return

    try {
      if (this.initialFocusDialogValue) {
        element.focus({ focusVisible: true })
      } else {
        element.focus({ preventScroll: true })
      }
    } catch {
      element.focus()
    }
  }
  deactivateFocusTrap() {
    // Invalidate any pending double-rAF from scheduleFocusTrap (e.g. open→compact).
    this.focusTrapGeneration = (this.focusTrapGeneration || 0) + 1
    releaseFocusTrap(this.trapElement, { restoreFocus: false })
    unlockBackground(this.element)
    unlockBodyScroll()
  }

  focusTriggerElement(event) {
    const trigger = event?.currentTarget

    if (trigger instanceof HTMLElement && typeof trigger.focus === "function") {
      return trigger
    }

    const active = document.activeElement

    if (active instanceof HTMLElement && active !== document.body) {
      return active
    }

    return null
  }

  restoreFocus() {
    const focusTarget =
      this.previouslyFocusedElement ||
      this.trapElement?._focusTrapPreviousElement

    if (
      focusTarget &&
      document.contains(focusTarget) &&
      typeof focusTarget.focus === "function"
    ) {
      focusTarget.focus()
    }

    this.previouslyFocusedElement = null
  }

  closeKeyPressed(event) {
    if (!CLOSE_KEYS.includes(event.key)) return false

    const active = document.activeElement
    if (!(active instanceof HTMLElement)) return false

    let node = active
    while (node instanceof HTMLElement) {
      if (this.elementHasModalCloseAction(node)) return true
      if (node === this.element) break
      node = node.parentElement
    }

    return false
  }

  elementHasModalCloseAction(element) {
    const action = element.dataset.action || ""
    return MODAL_CLOSE_ACTION_PATTERN.test(action)
  }

  focusTargetFromSelector(selector) {
    if (!this.hasInitialFocusSelectorValue || !selector) return null

    const target = this.contentTarget.querySelector(selector)
    if (target && typeof target.focus === "function") return target

    return null
  }
}
